import { SessionState, Web, UserAgent } from "sip.js";
import { removeIncomingCallAction } from "../sagas/callSessions/removeIncomingCall/action";
import { removeCallSessionAction } from "../sagas/callSessions/removeCallSession/action";
import { startCallRecordingAction } from "../sagas/callRecording/startCallRecording/action";
import { stopCallRecordingAction } from "../sagas/callRecording/stopCallRecording/action";
import { fetchCallHistoryAction } from "../sagas/callHistory/fetchCallHistory/action";
import { processCallRecordingAction } from "../sagas/callRecording/processCallRecording/action";
import { logCallCRMAction } from "../sagas/integration/logCallCrm/action";
import { selectActiveCall, selectActiveCallId, setActiveCallId, selectCallSessions } from "../state/callSessions/callSessionsSlice";
import { updateCallSessionAction } from "../sagas/callSessions/updateCallSession/action";
import { selectUserCredentials } from "../state/userCredential/userCredentialSlice";
import { showActiveCallScreen, setIsMergingCalls } from "../state/activeCallScreen/activeCallScreenSlice";
import { updateConferenceParticipantListAction } from "../sagas/conferenceCall/updateConferenceParticipantList/action";
import { RealTimeCallClient } from "../services/assistant/realtimeCallClient";
import { showSnackbar } from "../state/snackbar/snackbarSlice";
import { selectAudioDevices } from "../state/audioDevice/audioDeviceSlice";
import { selectDefaultMicrophone } from "../state/audioDevice/audioDeviceSlice";
import store from "../state/store";
import { getAudio, playRingTone, stopRingTone, stopRingbackTone } from "../utils/audioUtils";
import { normalizeNumber, isExternalNumber } from "../utils/phoneUtils";
import { selectAllowedAudioCodecs, selectRemovedAudioCodecs, selectAnalyticsMinDuration } from "../state/config/configSlice";
import removeCodecs from '../utils/removeCodecs'
import stripCodecs from "../utils/stripCodecs";

export default class CallSession {

    constructor(params) {
        this.direction = params.direction;
        this.id = params.id;
        this.displayName = params.displayName
        this.number = params.number;
        this.session = params.session;
        this.status = params.status;
        this.isAccepted = false;
        this.isOnHold = params.isOnHold;
        this.isMuted = params.isMuted;
        this.isConference = params.isConference;
        this.conferenceList = [];
        this.isRecording = false;
        this.parkId = null;
        this.conferenceBridgeId = null;
        this.cleanConferenceCall = null;
        this.from_tag = params.from_tag;
        this.callDuration = 0;
        this.hasEarlyMedia = false;
        this.crmContact = null;

        this.realtimeService = null;
        this.isStartingRealtime = false;
        this.isRealtime = false;
        this.crm = params.crm || 'default';

        this.callTimer = null;
        this.transferState = {
            isTransferSource: false,
            isTransferTarget: false,
            linkedSession: null
        };

        if (this.direction === 'incoming') {
            playRingTone();
        }

        // Set up state change listener
        this.session.stateChange.addListener((state) => {
            switch (state) {
                case SessionState.Establishing:
                    console.log("Call: SessionState.Establishing");
                    this.status = this.direction === 'incoming' ? 'incomingProgressing' : 'outgoingProgressing';
                    break;
                case SessionState.Established:
                    this.status = 'accepted';
                    this.isAccepted = true;

                    this.setupLocalMedia();
                    this.setupRemoteMedia();

                    stopRingTone();
                    stopRingbackTone();
                    this.startCallTimer();

                    break;
                case SessionState.Terminated:
                    console.log("Call: SessionState.Terminated");

                    if (this.isRealtime) {
                        this.stopRealtime();
                    }

                    //If conference call can't connect, we need to set setIsMergingCalls as false so active call screen can close.
                    if (this.isConference) {
                        store.dispatch(setIsMergingCalls(false));
                    }

                    if (this.transferState.linkedSession) {
                        // If this call was part of a transfer, reset both calls' states
                        const linkedCall = this.transferState.linkedSession;
                        this.resetTransferState();
                        linkedCall.resetTransferState();
                    }

                    if (this.status === 'incomingProgressing') {
                        store.dispatch(removeIncomingCallAction(this.id));
                    }
                    if (this.status !== 'ended') {
                        this.status = 'ended';
                        this.stopCallTimer();
                        store.dispatch(removeCallSessionAction({ callSessionId: this.id }));
                        const sessions = selectCallSessions(store.getState());
                        if (sessions.length === 0 || sessions.every(session => session.status === 'accepted')) {
                            stopRingTone();
                            stopRingbackTone();
                        }
                    }
                    if (this.isAccepted) {
                        const callId = this.id.replace(this.from_tag, '')
                        const callNumber = normalizeNumber(this.number);
                        const callType = this.direction;
                        const callDuration = this.callDuration;

                        if (isExternalNumber(callNumber)) {
                            store.dispatch(logCallCRMAction({ callId, callNumber, callType, callDuration }));
                        }
                        const analyticsMinDuration = selectAnalyticsMinDuration(store.getState());
                        if (callDuration >= (analyticsMinDuration || 40)) {
                            store.dispatch(processCallRecordingAction({ callId, callNumber, callType, callDuration }));
                        }
                    }
                    store.dispatch(fetchCallHistoryAction())
                    break;
                default:
            }
        });
    }

    setupLocalMedia() {
        if (!this.session) {
            throw new Error('Session does not exist.');
        }

        const mediaElement = getAudio('local');
        mediaElement.srcObject = this.session.sessionDescriptionHandler.localMediaStream;
    }

    setupRemoteMedia() {
        if (!this.session) {
            throw new Error('Session does not exist.');
        }

        const mediaElement = getAudio('remote');
        const remoteStream = this.session.sessionDescriptionHandler.remoteMediaStream;

        mediaElement.autoplay = true;
        mediaElement.srcObject = remoteStream;
    }

    toggleMute(shouldMute) {
        if (this.isMuted === shouldMute) return; // No change needed

        this.session.sessionDescriptionHandler.peerConnection
            .getSenders()
            .forEach((sender) => {
                if (sender.track && sender.track.kind === "audio") {
                    sender.track.enabled = !shouldMute;
                }
            });

        this.isMuted = shouldMute;
        console.log(`Call ${shouldMute ? "muted" : "unmuted"}`);
    }

    toggleHold(shouldHold) {
        const options = {
            sessionDescriptionHandlerModifiers: shouldHold ? [Web.holdModifier] : [],
        };

        this.session
            .invite(options)
            .then(() => {
                console.log(`Call ${shouldHold ? "placed on hold" : "taken off hold"}`);
                this.isOnHold = shouldHold;
                if (!shouldHold) {
                    this.setupRemoteMedia(); // Reattach remote media when unholding
                }
            })
            .catch((error) => {
                console.error(`Error ${shouldHold ? "placing call on" : "taking call off"} hold:`, error);
            });
    }


    accept() {
        // Stop ringing tone
        stopRingTone();

        // Get the current active call id and active call
        const activeCall = selectActiveCall(store.getState());
        const { speakers, microphones } = selectAudioDevices(store.getState())
        const defaultMicrophoneId = selectDefaultMicrophone(store.getState());

        const allowedCodecs = selectAllowedAudioCodecs(store.getState());
        const removedCodecs = selectRemovedAudioCodecs(store.getState());

        // Check if microphone and speaker is available
        if (microphones.length === 0 || speakers.length === 0) {
            store.dispatch(showSnackbar({
                message: microphones.length === 0 ?
                    'Cannot accept call: No microphone detected' :
                    'Cannot accept call: No speakers detected',
                severity: 'error'
            }));
            this.reject(); // Reject the call since we can't accept it
            return;
        }

        // If there is an active call and it's not the same as the current call, put it on hold
        if (activeCall && activeCall.id !== this.id && !activeCall.isOnHold) {
            activeCall.hold();
        }

        store.dispatch(setActiveCallId(this.id)); //setting id of this session as activeCallId

        // Accept the call
        // try {
        //     this.session.accept({
        //         media: {
        //             render: {
        //                 remote: getAudio('remote'),
        //                 local: getAudio('local'),
        //             },
        //         },
        //     });
        // } catch (e) {
        //     console.error(e);
        // }
        try {
            this.session.accept({
                sessionDescriptionHandlerOptions: {
                    constraints: {
                        audio: defaultMicrophoneId ? {
                            deviceId: { exact: defaultMicrophoneId }
                        } : true,
                        video: false
                    },
                },
                sessionDescriptionHandlerModifiers: [
                    allowedCodecs && allowedCodecs.length > 0 ? stripCodecs(allowedCodecs) : null,
                    removedCodecs && removedCodecs.length > 0 ? removeCodecs(removedCodecs) : null,
                ].filter(Boolean),

                media: {
                    render: {
                        remote: getAudio('remote'),
                        local: getAudio('local'),
                    },
                },
            });
        } catch (e) {
            console.error('Error accepting call:', e);
        }

        // Start the call timer and update the status
        this.startCallTimer();
        this.status = 'accepted';

        // Dispatch an action to show the Active Call Screen
        store.dispatch(showActiveCallScreen());

        // Dispatch an action to update the call session in the store
        store.dispatch(updateCallSessionAction())
    }
    //Note Bye is to end an active call (after it's been accepted)
    bye() {
        if (this.session
            && this.session.state !== SessionState.Terminated
            && this.session.state !== SessionState.Terminating
        ) {
            try {
                this.status = 'ended';
                this.session.bye();

                if (this.isRealtime) {
                    this.stopRealtime();
                }

                if (this.cleanConferenceCall) {
                    this.cleanConferenceCall();
                }

                this.stopCallTimer()
                store.dispatch(removeCallSessionAction({ callSessionId: this.id }));
                const sessions = selectCallSessions(store.getState());
                if (sessions.length === 0 || sessions.every(session => session.status === 'accepted')) {
                    stopRingTone();
                    stopRingbackTone();
                }

            } catch (e) {
                console.error(e);

            }
        }
    }
    //Note Cancel is outgoing calls that haven't been established yet.
    cancel() {
        if (this.session && (this.session.state === SessionState.Initial
            || this.session.state === SessionState.Establishing
        )) {
            try {
                this.status = 'ended';
                this.session.cancel();

                this.stopCallTimer()
                store.dispatch(removeCallSessionAction({ callSessionId: this.id }));
                const sessions = selectCallSessions(store.getState());
                if (sessions.length === 0 || sessions.every(session => session.status === 'accepted')) {
                    stopRingTone();
                    stopRingbackTone();
                }

            } catch (e) {
                console.error(e);

            }
        }

    }
    ignore() {
        this.status = 'ended';
        store.dispatch(removeCallSessionAction({ callSessionId: this.id }));
        this.stopCallTimer()
        const sessions = selectCallSessions(store.getState());
        if (sessions.length === 0 || sessions.every(session => session.status === 'accepted')) {
            stopRingTone();
            stopRingbackTone();
        }

    }
    //Note Reject is for incoming calls that haven't been answered yet.
    reject() {
        const { uid } = selectUserCredentials(store.getState());

        try {
            this.status = 'ended';
            this.session.reject();
            this.stopCallTimer()
            store.dispatch(removeCallSessionAction({ callSessionId: this.id }));
            const sessions = selectCallSessions(store.getState());
            if (sessions.length === 0 || sessions.every(session => session.status === 'accepted')) {
                stopRingTone();
                stopRingbackTone();
            }

        } catch (e) {
            console.error(e);
        }

    }

    cleanup() {
        // For sessions that are already terminated but still in state
        if (this.status === 'ended') {
            console.log('Cleaning up ended session');
            this.stopCallTimer();
            store.dispatch(removeCallSessionAction({ callSessionId: this.id }));
            const sessions = selectCallSessions(store.getState());
            if (sessions.length === 0 || sessions.every(session => session.status === 'accepted')) {
                stopRingTone();
                stopRingbackTone();
            }
        }
    }

    mute() {
        this.toggleMute(true);
    }

    unmute() {
        this.toggleMute(false);
    }

    hold() {
        this.toggleHold(true);
    }


    unhold() {
        this.toggleHold(false);
    }

    startRecording() {
        this.isRecording = true
        const callId = this.id.replace(this.from_tag, '') // We need to remove from_tag from id.
        store.dispatch(startCallRecordingAction(callId));
    }

    stopRecording() {
        this.isRecording = false
        const callId = this.id.replace(this.from_tag, '') // We need to remove from_tag from id.
        store.dispatch(stopCallRecordingAction(callId));
    }


    sendDTMF(digit) {
        try {
            const peerConnection = this.session.sessionDescriptionHandler.peerConnection;
            const dtmfSender = peerConnection.getSenders().find(sender => sender.track && sender.track.kind === 'audio').dtmf;

            if (dtmfSender) {
                dtmfSender.insertDTMF(digit, 100, 50);
                console.log(`DTMF sent: ${digit}`);
            } else {
                console.error("DTMF sender is not available");
            }
        } catch (error) {
            console.error("Failed to send DTMF:", error);
        }
    }
    initiateAssistedTransfer() {
        this.transferState = {
            isTransferSource: true,
            isTransferTarget: false,
            linkedSession: null
        };
    }

    setAsTransferTarget(sourceSession) {
        this.transferState = {
            isTransferSource: false,
            isTransferTarget: true,
            linkedSession: sourceSession
        };
        sourceSession.transferState.linkedSession = this;
    }
    completeAssistedTransfer() {
        if (this.transferState.isTransferSource && this.transferState.linkedSession) {
            try {
                this.transfer(this.transferState.linkedSession, false);
            } catch (error) {
                console.error('Failed to complete assisted transfer:', error);
            }
        }
    }

    cancelAssistedTransfer() {
        if (this.transferState.isTransferSource && this.transferState.linkedSession) {
            try {
                const targetCall = this.transferState.linkedSession;
                // End the target call
                targetCall.bye();
                // Reset states on both calls
                this.resetTransferState();
                targetCall.resetTransferState();
            } catch (error) {
                console.error('Failed to cancel assisted transfer:', error);
                this.resetTransferState();
            }
        }
    }

    resetTransferState() {
        this.transferState = {
            isTransferSource: false,
            isTransferTarget: false,
            linkedSession: null
        };
    }

    transfer(target, blind) {
        if (blind) {
            const { domain } = selectUserCredentials(store.getState());
            const isConferenceTransfer = target.includes("adhoc");
            const targetURI = isConferenceTransfer
                ? UserAgent.makeURI(target)
                : UserAgent.makeURI(`sip:${target}@${domain}`);
            this.session
                .refer(targetURI)
                .then(() => {
                    console.log("Transfer initiated successfully");
                    // Update the conference participant list if session is transferred to conference call
                    if (isConferenceTransfer) {
                        setTimeout(() => {
                            store.dispatch(updateConferenceParticipantListAction({ conferenceUri: target }));
                        }, 2000);
                    }
                })
                .catch((error) => {
                    console.error("Failed to initiate transfer:", error);
                });
        }
        else {
            try {
                this.session.refer(target.session);
                store.dispatch(showSnackbar({
                    message: "Transfer completed",
                    severity: "success"
                }));
            } catch (e) {
                console.error('Fail to assisted refer', e);
                store.dispatch(showSnackbar({
                    message: "Transfer failed",
                    severity: "error"
                }));
            }

        }
    }

    async startRealtime(onMessage) {
        this.isStartingRealtime = true;

        if (!this.session?.sessionDescriptionHandler) return;

        const remoteStream = this.session.sessionDescriptionHandler.remoteMediaStream;
        // const sipPeerConnection = this.session.sessionDescriptionHandler.peerConnection;

        this.realtimeService = new RealTimeCallClient();

        this.realtimeService.setCallMetadata({
            callId: this.id.replace(this.from_tag, ''),
            callNumber: normalizeNumber(this.number),
            callDirection: this.direction,
            displayName: this.displayName,
            crm: this.crm,
        });

        try {
            await this.realtimeService.start(
                remoteStream,
                // sipPeerConnection,
                onMessage,
            );
            this.isStartingRealtime = false;

            this.isRealtime = true;
        } catch (err) {
            console.error('Failed to start realtime assistant:', err);
            this.realtimeService = null;
            this.isStartingRealtime = false;
            this.isRealtime = false;
        }
    }
    stopRealtime() {
        this.realtimeService?.stop();
        this.realtimeService = null;
        this.isRealtime = false;
    }

    sendRealtimeMessage(text) {
        this.realtimeService?.sendMessage(text);
    }



    startCallTimer() {
        if (!this.callDuration && !this.callTimer) {
            this.callTimer = setInterval(() => {
                this.callDuration += 1
                store.dispatch(updateCallSessionAction())
            }, 1000)

        }
    }

    stopCallTimer() {
        if (this.callTimer) {
            clearInterval(this.callTimer)
        }
    }
}
